import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { AlertDialog } from "@/widgets/AlertDailog";
import { ConfirmationDialog } from "@/widgets/AlertCommon";
import { useGetRules, useToggleRuleValueStatus } from "@/hooks/useLocalStorage";
import type { OfferForm, RuleForm } from "@/shared/partnerschema";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ruleTypeOptions = [
    { value: "0", label: "Eligibility Rules" },
    { value: "1", label: "Pricing Rules" },
    { value: "2", label: "Credit Rating Rules" },
    { value: "4", label: "Workflow Entry Conditions" },
    { value: "5", label: "Compliance Rules" },
    { value: "6", label: "Form Rules" },
];

function getRuleTypeLabel(type: number | string): string {
    const match = ruleTypeOptions.find((opt) => opt.value === String(type));
    return match ? match.label : `Type ${type}`;
}

function formatDateTime(dateStr: string): string {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
}

// ─── Rule content viewer popup ────────────────────────────────────────────────

function RuleViewPopup({
    open,
    title,
    content,
    onClose,
}: {
    open: boolean;
    title: string;
    content?: string;
    onClose: () => void;
}) {
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-center">{title}</DialogTitle>
                </DialogHeader>
                <div className="border rounded-md p-4 bg-gray-50 overflow-auto flex-1">
                    <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
                        {content || "No content available"}
                    </pre>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface RulesSetupProps {
    open: boolean;
    onClose: () => void;
    offerData?: OfferForm;
    /** When true: only show rules with Version > 0, hide Create/Modify action */
    viewType?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

function RulesSetup({ open, onClose, offerData, viewType }: RulesSetupProps) {
    const [, navigate] = useLocation();

    // ── Data ──────────────────────────────────────────────────────────────────
    const { data: rulesData, isLoading, refetch } = useGetRules(
        1,
        50,
        open ? offerData?.Id : undefined   // only fetch when dialog is open
    );

    const rules: RuleForm[] = rulesData?.items ?? [];

    const displayedRules = viewType
        ? rules.filter((rule) => rule.Version && rule.Version > 0)
        : rules;

    // ── Toggle publish mutation ───────────────────────────────────────────────
    const toggleMutation = useToggleRuleValueStatus();

    // ── Local UI state ────────────────────────────────────────────────────────
    const [pendingToggleRule, setPendingToggleRule] = useState<RuleForm | null>(null);

    const [viewPopup, setViewPopup] = useState({
        open: false,
        title: "",
        content: "",
    });

    const [alertData, setAlertData] = useState({
        open: false,
        message: "",
        status: false,
    });

    // Re-fetch whenever the dialog opens with a new offer
    useEffect(() => {
        if (open && offerData?.Id) {
            refetch();
        }
    }, [open, offerData?.Id]);

    // Handle toggle mutation result
    useEffect(() => {
        if (toggleMutation.isSuccess) {
            const wasPublished = pendingToggleRule?.Approved === 1;
            setAlertData({
                open: true,
                status: true,
                message: wasPublished
                    ? `"${pendingToggleRule?.Name}" has been unpublished successfully.`
                    : `"${pendingToggleRule?.Name}" has been published successfully.`,
            });
            setPendingToggleRule(null);
            refetch();
            toggleMutation.reset();
        } else if (toggleMutation.isError) {
            setAlertData({
                open: true,
                status: false,
                message: "Failed to update publish status. Please try again.",
            });
            setPendingToggleRule(null);
            toggleMutation.reset();
        }
    }, [toggleMutation.isSuccess, toggleMutation.isError]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const openViewPopup = (title: string, content: string) =>
        setViewPopup({ open: true, title, content });

    const closeViewPopup = () =>
        setViewPopup({ open: false, title: "", content: "" });

    const handleNavigateToVersion = (rule: RuleForm) => {
        navigate(`/createversion/${rule.Rule_Id}`, {
            state: { id: rule.Rule_Id, object: rule, offerData },
        } as any);
    };

    const handleToggleClick = (rule: RuleForm) => {
        setPendingToggleRule(rule);
    };

    const handleToggleConfirm = () => {
        if (!pendingToggleRule || !offerData?.Id) return;
        toggleMutation.mutate({
            objverid: String(pendingToggleRule.ObjectVersionId),
            offId: String(offerData.Id),
        });
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <>
            <Dialog open={open} onOpenChange={onClose}>
                <DialogContent className="max-w-7xl">
                    <DialogHeader>
                        <DialogTitle className="text-center text-lg font-bold">
                            RAW RULE SETUP
                            {offerData?.ExchangeOfferName && (
                                <span className="ml-2 text-sm font-normal text-muted-foreground">
                                    ({offerData.ExchangeOfferName})
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="border rounded-md overflow-auto max-h-[80vh]">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Rule ID</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Rule Type</TableHead>
                                    <TableHead>Raw Rule</TableHead>
                                    <TableHead>Rule With Values</TableHead>
                                    <TableHead>Version</TableHead>
                                    <TableHead>Modified Date & Time</TableHead>
                                    <TableHead>Action</TableHead>
                                    <TableHead>Publish Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center py-6 text-muted-foreground">
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : displayedRules.length > 0 ? (
                                    displayedRules.map((rule, index) => (
                                        <TableRow key={rule.Rule_Id ?? index}>
                                            <TableCell>{rule.Rule_Id}</TableCell>

                                            <TableCell className="whitespace-nowrap">
                                                {rule.Name}
                                            </TableCell>

                                            <TableCell className="whitespace-nowrap">
                                                {getRuleTypeLabel(rule.Type)}
                                            </TableCell>

                                            {/* Raw Rule */}
                                            <TableCell>
                                                <Button
                                                    variant="link"
                                                    className="p-0 h-auto"
                                                    onClick={() => openViewPopup("Raw Rule", rule.raw_rule)}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>

                                            {/* Rule With Values — only if version exists */}
                                            <TableCell>
                                                {rule.Version ? (
                                                    <Button
                                                        variant="link"
                                                        className="p-0 h-auto"
                                                        onClick={() =>
                                                            openViewPopup(
                                                                "Rule With Values",
                                                                rule.rule_with_value ?? ""
                                                            )
                                                        }
                                                    >
                                                        View
                                                    </Button>
                                                ) : (
                                                    <span className="text-muted-foreground text-sm">—</span>
                                                )}
                                            </TableCell>

                                            <TableCell>{rule.Version ?? "—"}</TableCell>

                                            <TableCell className="whitespace-nowrap">
                                                {formatDateTime(rule.DateTimeChanged)}
                                            </TableCell>

                                            {/* Action */}
                                            <TableCell>
                                                <div className="flex gap-2">
                                                    {!viewType && (
                                                        <Button
                                                            variant="link"
                                                            className="p-0 h-auto"
                                                            onClick={() => handleNavigateToVersion(rule)}
                                                        >
                                                            {rule.Version ? "Modify" : "Create"}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="link"
                                                        className="p-0 h-auto"
                                                        disabled={!rule.Version}
                                                        onClick={() => handleToggleClick(rule)}
                                                    >
                                                        {rule.Approved ? "Unpublish" : "Publish"}
                                                    </Button>
                                                </div>
                                            </TableCell>

                                            {/* Publish Status */}
                                            <TableCell>
                                                <span
                                                    className={`text-sm font-medium ${
                                                        rule.Approved ? "text-green-600" : "text-gray-400"
                                                    }`}
                                                >
                                                    {rule.Approved ? "Published" : "Unpublished"}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center py-6 text-muted-foreground">
                                            {viewType
                                                ? "No Rule Value has Been Set Up"
                                                : "No rules found"}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Rule content viewer */}
            <RuleViewPopup
                open={viewPopup.open}
                title={viewPopup.title}
                content={viewPopup.content}
                onClose={closeViewPopup}
            />

            {/* Publish / Unpublish confirm */}
            <ConfirmationDialog
                open={!!pendingToggleRule}
                onOpenChange={(o) => { if (!o) setPendingToggleRule(null); }}
                onConfirm={handleToggleConfirm}
                isPending={toggleMutation.isPending}
                heading={
                    pendingToggleRule?.Approved
                        ? "Unpublish Rule"
                        : "Publish Rule"
                }
                message={
                    pendingToggleRule?.Approved
                        ? `Are you sure you want to unpublish "${pendingToggleRule?.Name}"?`
                        : `Are you sure you want to publish "${pendingToggleRule?.Name}"?`
                }
                confirmLabel={pendingToggleRule?.Approved ? "Unpublish" : "Publish"}
            />

            {/* Success / error alert */}
            <AlertDialog
                open={alertData.open}
                onOpenChange={(o) => setAlertData((p) => ({ ...p, open: o }))}
                message={alertData.message}
                status={alertData.status}
            />
        </>
    );
}

export default RulesSetup;